import { Box, Paper, useTheme } from '@mui/material';
import { Modal } from '@mui/material';
import { Typography } from '@mui/material';
import { IconButton } from '@mui/material';
import { useRef, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@verantos-dev/core-annotation-tool';
import { selectBatchId } from '@verantos-dev/feature-supervisor-queue';
import { Button, Icon } from '@verantos-dev/shared-components';
import { ErrorAlertCard } from '@verantos-dev/ui-annotation-tool';

import { useMarkSupervisionCompleteMutation } from '../../../api/supervisorApiSlice';
import { selectMarkSupervisionCompleteError } from '../../../state/selector';
import {
  clearConceptAnnotationIdsInEditMode,
  setMarkSupervisionCompleteError,
  clearMarkSupervisionCompleteError,
} from '../../../state/supervisorSlice';

interface CompleteSupervisionModalProps {
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
}

export const CompleteSupervisionModal = ({
  showModal,
  setShowModal,
}: CompleteSupervisionModalProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const { encounterId } = useParams();
  const [markSupervisionComplete, { isLoading: isMarkSupervisionCompleteLoading }] =
    useMarkSupervisionCompleteMutation();
  const dispatch = useAppDispatch();
  const error = useAppSelector(selectMarkSupervisionCompleteError);
  const batchId = useAppSelector(selectBatchId);
  const errorRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  const handleMarkSupervisionComplete = async () => {
    if (!encounterId || !batchId) {
      return;
    }

    try {
      dispatch(clearMarkSupervisionCompleteError());
      await markSupervisionComplete({ encounterId, batchId }).unwrap();
      setShowModal(false);
      dispatch(clearConceptAnnotationIdsInEditMode());
    } catch (err: any) {
      const message =
        err?.data?.detail ||
        err?.data?.message ||
        err?.data?.errorMessage ||
        err?.message ||
        'Failed to mark supervision task as complete. Please try again.';
      dispatch(setMarkSupervisionCompleteError(message));
    }
  };
  return (
    <Modal
      open={showModal}
      onClose={() => setShowModal(false)}
      aria-labelledby="mark-supervision-task-complete-modal-title"
    >
      <Paper
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '600px',
          p: theme.spacing(6),
          outline: 'none',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography id="mark-supervision-task-complete-modal-title" variant="h6">
              {formatMessage({ id: 'supervisorReview.markSupervisionTaskCompleteModal.title' })}
            </Typography>
            <IconButton onClick={() => setShowModal(false)} size="small">
              <Icon id="mark-supervision-task-complete-modal-close-icon" icon="clear" width={16} />
            </IconButton>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(4) }}>
            <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
              {formatMessage({
                id: 'supervisorReview.markSupervisionTaskCompleteModal.description',
              })}
            </Typography>
            <Box ref={errorRef}>
              <ErrorAlertCard
                errorMessage={error ?? ''}
                onDismiss={() => dispatch(clearMarkSupervisionCompleteError())}
              />
            </Box>
            {isMarkSupervisionCompleteLoading && (
              <Typography variant="body1">
                {formatMessage({
                  id: 'supervisorReview.markSupervisionTaskCompleteModal.finalizingMessage',
                })}
              </Typography>
            )}
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                gap: theme.spacing(3),
                justifyContent: 'flex-end',
              }}
            >
              <Button
                id="cancel-finalized-annotation-button"
                displayLabel={formatMessage({
                  id: 'supervisorReview.markSupervisionTaskCompleteModal.cancelButton',
                })}
                variant="text"
                sx={{
                  fontSize: '15px',
                  textTransform: 'none',
                }}
                onClickHandler={() => setShowModal(false)}
                disabled={isMarkSupervisionCompleteLoading}
              />
              <Button
                id="confirm-finalized-annotation-button"
                displayLabel={formatMessage({
                  id: 'supervisorReview.markSupervisionTaskCompleteModal.confirmButton',
                })}
                variant="contained"
                sx={{
                  fontSize: '15px',
                  textTransform: 'none',
                  backgroundColor: theme.palette.primary.main,
                }}
                onClickHandler={handleMarkSupervisionComplete}
                disabled={isMarkSupervisionCompleteLoading}
              />
            </Box>
          </Box>
        </Box>
      </Paper>
    </Modal>
  );
};
